/**
 * The design tokens. Since the 2026-10 redesign the account area wears the site's
 * Apple v3 identity, direction « Filets » (Claude Design project « Account »,
 * identite.md §2): white page, ink #1D1D1F, Apple greys, hairlines #E0E0E5,
 * field borders #86868B for 3:1, a destructive red #C4282D white text reads on,
 * the system font stack. The names below are the old ones, kept so 28k lines of
 * inline styles follow without a rename; their values are the redesign's.
 *
 * Before that they were verbatim from the handoff (`design_handoff_airlock_web/
 * components/tokens.ts`). They are the same values as the desktop app's palette
 * (`desktop/src/renderer/src/theme.ts`), which is what makes the site, the docs,
 * the account area and the app read as one product rather than four.
 *
 * The rule the palette keeps: no warm neutrals. Any new surface is `page` or
 * `surfaceAlt`, any new border is `line` or `lineStrong`, and amber appears only
 * as status — never as chrome.
 *
 * Deliberately copied into each of the three web projects rather than shared:
 * `site/`, `docs/` and `account/` deploy on their own clocks, and a token change
 * that silently redeployed all three is not a saving.
 */
export const color = {
  page: '#FFFFFF',
  surface: '#FFFFFF',
  /** The one secondary light surface: hovers, footer, tracks, code chips. */
  surfaceAlt: '#F5F5F7',
  line: '#E0E0E5',
  lineStrong: '#D2D2D7',
  lineInput: '#86868B',
  ink: '#1D1D1F',
  inkBody: '#424245',
  inkMuted: '#515154',
  inkSubtle: '#6E6E73',
  inkFaint: '#6E6E73',
  inkGhost: '#AEAEB2',

  accent: '#1FB6A6',
  accentHi: '#22C3A6',
  accentInk: '#08120F',
  accentText: '#0E7C70',
  accentWash: 'rgba(31,182,166,.14)',

  dark: '#0F1319',
  darkPanel: '#171C24',
  darkWell: '#0B0E13',
  darkRaised: '#1E242E',
  darkLine: '#2A323E',
  darkHover: '#141A22',
  darkText: '#E7EAEF',
  darkMuted: '#9BA6B4',
  darkDim: '#606B7A',
  darkFaint: '#4E5765',

  blue: '#4C8DFF',
  purple: '#A277FF',
  yellow: '#F2C94C',
  orange: '#FF8A3D',
  red: '#C4282D',
  green: '#2FA96A',

  okText: '#0B5F56',
  okBg: '#E0F5F3',
  warnText: '#8A6A2A',
  warnBg: '#FDF4DE',
  offText: '#515154',
  offBg: '#F5F5F7',
  badText: '#8A2A2A',
  badBg: '#FBE9E9',
  badLine: '#F0DADA',
  badSoft: '#FBF3F3',
  noteAmberBg: '#FDF8EA',
  noteTealBg: '#F3FAF8',
  placeholderBg: '#F5E9C9',
  placeholderInk: '#5A4A17',

  /* ── the shell's own surfaces ─────────────────────────────────────────────
   *
   * Four values the marketing site and the docs have no use for, and that the
   * account area cannot draw without. They are additions, not a second palette:
   * every hue above is unchanged, and nothing here is a lighter or darker
   * restatement of a token that already exists.
   *
   * `desk` is what the shell floats on, `page` above is the column inside it,
   * and `hairSoft` divides rows *within* a list where `line` would draw a grid.
   * `accentRule` is the teal at border strength — the redesign spends teal on
   * an outline, a tint and a state dot, and never on a fill, so it needs the
   * one weight the palette was missing. */
  desk: '#FFFFFF',
  shellPage: '#FFFFFF',
  hairSoft: '#E0E0E5',
  accentRule: 'rgba(31,182,166,.35)'
} as const;

export const font = {
  sans: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", system-ui, sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, monospace'
} as const;

/** The type ramp, in px. */
export const type = {
  hero: 66,
  sectionLead: 44,
  panelLead: 38,
  h1: 40,
  h2Feature: 34,
  h1App: 30,
  price: 52,
  priceApp: 44,
  statLarge: 30,
  statApp: 26,
  h2: 22,
  h2Legal: 20,
  lede: 20,
  ledeDocs: 18,
  cardTitle: 16,
  body: 15.5,
  bodyAlt: 14.5,
  ui: 14,
  uiSmall: 13.5,
  uiSmaller: 13,
  meta: 12.5,
  mono: 12,
  monoSmall: 11,
  kicker: 10.5
} as const;

export const radius = { chip: 999, sm: 6, md: 8, lg: 9, xl: 12, xxl: 14, panel: 16 } as const;

export const shadow = {
  card: '0 1px 2px rgba(18,22,28,.06)',
  tab: '0 1px 2px rgba(0,0,0,.08)',
  menu: '0 0 0 1px rgba(0,0,0,.06), 0 18px 40px -16px rgba(0,0,0,.3)',
  banner: '0 20px 50px -20px rgba(0,0,0,.45)',
  mock: '0 30px 80px -30px rgba(15,19,25,.5)'
} as const;

/** Max content widths. */
export const layout = {
  site: 1160,
  siteWide: 1240,
  article: 740,
  legalSidebar: 238
} as const;
